import { createContext, Match, Switch, createEffect, createSignal, onCleanup, Show, For, createMemo } from 'solid-js';
import { date, requireContext } from 'common';
import { debounce } from "common"
import { scheduleGrid as api } from 'st-app/api'
import { CalendarContext, STScheduleGridEvents } from './CalendarContext';
import { MiniCalendar, MiniNavigation } from './fragments/MiniCalendar';
import styles from './st-schedule-grid.module.css';
import { Button, ButtonGroup, Checkbox, Icon, RadioGroup, useViewContext } from '../..';
import { Day } from './fragments/Day';

export const ST = (window as any).ST;
export type CalendarDisplayMode = 'Month' | 'Week' | 'WorkWeek' | 'Day';

const CalendarContextJSX = createContext<CalendarContext>();
export const requireCalendarContext = () => requireContext(CalendarContextJSX);

class STScheduleGridProps {
    displayMode: CalendarDisplayMode = 'Month';
    viewId: string = '';
    objectId: string = '';
    identity: string = '';
    afterAddClicked?: boolean = false;
    enabled: boolean = false;

    events?: STScheduleGridEvents = new STScheduleGridEvents();
}

export default function STScheduleGrid(props: STScheduleGridProps) {
    const options = new CalendarContext(props.events, props.afterAddClicked);

    return <CalendarContextJSX.Provider value={options}>
        <CalendarInternal {...props} />
    </CalendarContextJSX.Provider>
}

function CalendarInternal(props: STScheduleGridProps) {
    const viewCtx = useViewContext();
    const ctx = requireCalendarContext();

    createEffect(() => ctx.setDisplayMode(props.displayMode));

    createEffect(() => {
        ctx.isEnabled[1](props.enabled);
    });

    createEffect(() => {
        const appts = ctx.appointments[0]();
        if (appts && props.events?.onUpdate) {
            requestAnimationFrame(props.events.onUpdate);
        }
    });

    createEffect(() => {
        const first = ctx.firstDate();
        const last = ctx.lastDate();

        if (first && ctx.day().getTime() < first.getTime()) {
            ctx.setDay(first);
        }
        if (last && ctx.day().getTime() > last.getTime()) {
            ctx.setDay(last);
        }
    });

    createEffect(() => {
        const viewId = props.viewId;
        const objectId = props.objectId;

        if (viewId && objectId) {
            (async () => {
                const config = await api.FetchCalendar(viewId, objectId);
                ctx.setLegends(config.legends);
                ctx.setBaseCalendar(config.calendar);
            })();
        }
    });

    createEffect(() => {
        const day = date.toString(ctx.day());
        if (props.events?.onDateChanged) {
            props.events?.onDateChanged(day);
        }
    });

    const fetchAppointments = debounce(async (viewId: string, objectId: string, identity: string) => {
        const apptData = await api.FetchAppointments(viewId, objectId, identity);
        ctx.setAdjCalendar(apptData.calendar);
        ctx.appointments[1](apptData.appointments.map(appt => ({
            ...appt,
        })));
    }, 150);

    if (props.events) {
        let origIdentity = props.identity;
        props.events.refresh = () => {
            if (origIdentity !== props.identity) {
                ctx.appointments[1]([]);
                fetchAppointments(props.viewId, props.objectId, props.identity);
                origIdentity = props.identity;
            }
            else if (viewCtx && origIdentity != viewCtx.identity()) {
                ctx.appointments[1]([]);

                fetchAppointments(props.viewId, props.objectId, viewCtx.identity());
                origIdentity = viewCtx.identity();
            }
        };
    }

    createEffect(() => {
        const viewId = props.viewId;
        const objectId = props.objectId;
        const identity = props.identity;

        ctx.colorIndices.clear();
        ctx.colorIndex = 0;

        if (identity) {
            fetchAppointments(viewId, objectId, identity);
        }
        else {
            ctx.appointments[1]([]);
        }
    });

    return <div class={`${styles.schedule} flexbox vertical`} style="min-width: 95%">
        <Header />
        <div class="flexbox horizontal auto">
            <CalendarContent />
            <NavPanel />
        </div>
    </div>
}
STScheduleGrid.defaultProps = new STScheduleGridProps();

function Header() {
    const ctx = requireCalendarContext();

    function move(distance: number) {
        switch (ctx.displayMode()) {
            case 'Month':
                ctx.setDay(date.addMonths(ctx.day(), distance));
                break;
            case 'WorkWeek':
            case 'Week':
                ctx.setDay(date.addDays(ctx.day(), distance * 7));
                break;
            case 'Day':
                ctx.setDay(date.addDays(ctx.day(), distance));
                break;
        }
    }

    const longDate = createMemo(() => {
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }).format(ctx.day());
    });

    return <div class={`${styles['main-heading']} flexbox horizontal middle`}>
        <ButtonGroup>
            <Button type="primary" onClick={() => ctx.setDay(date.today())}>Today</Button>
            <Button label="Previous" icon="fa fa-chevron-left" onClick={() => move(-1)} />
            <Button label="Next" icon="fa fa-chevron-right" onClick={() => move(1)} />
        </ButtonGroup>
        <div class={`${styles['month-title']} auto flexbox horizontal middle`}>
            <span style="margin: auto">
                <Switch>
                    <Match when={ctx.displayMode() === 'Day'}>
                        {longDate()}
                    </Match>
                    <Match when={ctx.displayMode() !== 'Day'}>
                        {date.monthName(ctx.day())}
                        &nbsp;
                        {ctx.day().getFullYear()}
                    </Match>
                </Switch>
            </span>
        </div>
        <RadioGroup type="button-group" value={[ctx.displayMode, ctx.setDisplayMode]}>
            <RadioGroup.Option label="Day" value="Day" />
            <RadioGroup.Option label="Work Week" value="WorkWeek" />
            <RadioGroup.Option label="Week" value="Week" />
            <RadioGroup.Option label="Month" value="Month" />
        </RadioGroup>
    </div>
}

function CalendarContent() {
    const ctx = requireCalendarContext();

    function Week(props: { date: Date }) {
        const ctx = requireCalendarContext();

        const days = createMemo(() => {
            const dt = date.startOfWeek(props.date);
            return [0, 1, 2, 3, 4, 5, 6].map(i => date.addDays(dt, i));
        });

        return <>
            <For each={days()}>{(d) => <Day date={d} />}</For>
        </>
    }

    function WorkWeek(props: { date: Date }) {
        const ctx = requireCalendarContext();

        const days = createMemo(() => {
            const dt = date.startOfWeek(props.date);
            return [1, 2, 3, 4, 5].map(i => date.addDays(dt, i));
        });

        return <>
            <For each={days()}>{(d) => <Day date={d} />}</For>
        </>
    }

    return <div class={`${styles.calendar} auto`} data-display-mode={ctx.displayMode()}>
        <Switch>
            <Match when={ctx.displayMode() === 'Month'}>
                <MonthHeading />
                <For each={ctx.weeks()}>{(w) => <Week date={w} />}</For>
            </Match>
            <Match when={ctx.displayMode() === 'Week'}>
                <MonthHeading />
                <Week date={ctx.day()} />
            </Match>
            <Match when={ctx.displayMode() === 'WorkWeek'}>
                <MonthHeading excludeWeekend />
                <WorkWeek date={ctx.day()} />
            </Match>
            <Match when={ctx.displayMode() === 'Day'}>
                <Day date={ctx.day()} />
            </Match>
        </Switch>
    </div>
}

function MonthHeading(props: { excludeWeekend?: boolean }) {
    let start = date.startOfWeek(date.today());
    const days = !!props.excludeWeekend
        ? [1, 2, 3, 4, 5].map(i => date.addDays(start, i))
        : [0, 1, 2, 3, 4, 5, 6].map(i => date.addDays(start, i));

    const [dateFormat, setDateFormat] = createSignal<'long' | 'short' | 'narrow'>('long');

    const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
            const { width, height } = entry.contentRect;
            setDateFormat(width < 80 ? 'short' : 'long');
        }
    });
    onCleanup(() => resizeObserver.disconnect());

    function init(el: HTMLDivElement) {
        resizeObserver.observe(el);
    }

    return <For each={days}>{d =>
        <div ref={init} class={styles.heading}>
            {Intl.DateTimeFormat('en-US', { weekday: dateFormat() }).format(d)}
        </div>
    }</For>
}

function NavPanel() {
    return <div class="flexbox vertical">
        <div class={styles['mini-calendar-container']}>
            <MiniNavigation />
            <MiniCalendar />
        </div>
        <Legend />
    </div>
}

function Legend() {
    const ctx = requireCalendarContext();

    function isEnabled(key: string) {
        const enabled = ctx.enabledLegendItems()[key];
        return enabled === undefined ? true : enabled;
    }

    function toggle(key: string) {
        const enabled = !isEnabled(key);
        ctx.setEnabledLegendItems({ ...ctx.enabledLegendItems(), [key]: enabled});
    }

    return <div>
        <For each={ctx.legends()}>{legend =>
            <div class={styles.legend}>
                <h2>{legend.title}</h2>
                <ul>
                    <For each={legend.items}>{it =>
                        <li>
                            <label class="flexbox horizontal middle padded">
                                <input type="checkbox" checked={isEnabled(it.key ?? '')} onchange={() => toggle(it.key ?? '')} />
                                <span class={styles['legend-checkbox']}>
                                    <Icon size={24} src={it.iconPath ?? ''} />
                                </span>
                                <span class="auto">
                                {it.description}
                                </span>
                            </label>
                        </li>
                    }</For>
                </ul>
            </div>
        }</For>
    </div>
}
